import React from 'react';
import HeaderTable from '../headerTable/headerTable';
import TabsTable from '../tabs/tabsTable';
import AccordionTable from '../accordion/accordionTable';
import NoDataMessage from '../noDataMessage/noDataMessage';
import { openLinkParent } from '../../../functions/functions';

/**
 * Verifica si la fecha actual está dentro del rango de startDate y endDate
 * @param {string} startDate - Fecha de inicio en formato ISO (ej: "2025-07-12Z")
 * @param {string} endDate - Fecha de fin en formato ISO (ej: "2025-11-10Z")
 * @returns {boolean} true si la fecha actual está dentro del rango
 */
const isCurrentDateInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  
  try {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    
    const normalizeDate = (dateStr) => {
      if (dateStr.endsWith('Z') && !dateStr.includes('T')) {
        return dateStr.replace('Z', 'T00:00:00Z');
      }
      return dateStr;
    };
    
    const start = new Date(normalizeDate(startDate));
    const end = new Date(normalizeDate(endDate));
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return false;
    }
    
    return today >= start && today <= end;
  } catch (error) {
    console.warn('Error al verificar rango de fechas:', error);
    return false;
  }
};

/**
 * Componente principal para mostrar tablas de posiciones de fútbol
 * Se adapta automáticamente entre tabs y accordion según la cantidad de stages
 * Renderiza la tabla de posiciones con estadísticas completas de equipos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.dataTab1 - Datos de la tabla de posiciones
 * @param {Array} props.dataTab1.soccerStages - Array de stages/divisiones del torneo
 * @param {Array} props.dataTab1.soccerStages[].division - Divisiones dentro de cada stage
 * @param {Array} props.dataTab1.soccerStages[].division[].ranking - Rankings de equipos
 * 
 * @returns {JSX.Element} Tabla de posiciones adaptativa (tabs o accordion)
 */
export default function TableFutbol({ dataTab1, sport, version }) {

  const stages = (dataTab1[`${sport}Stages`] || []).filter(stage =>
    stage?.division?.[0]?.ranking?.length > 0
  );
  const useTabs = stages.length <= 2;
  
  // Calcula el índice del tab activo basándose en las fechas
  // Si no hay fechas válidas en ningún stage, usa 0 como valor por defecto
  const activeTabIndex = stages.findIndex(stage => 
    stage.startDate && stage.endDate && isCurrentDateInRange(stage.startDate, stage.endDate)
  );
  const initialActiveTab = activeTabIndex >= 0 ? activeTabIndex : 0;
  
  const renderDivisionTable = (division) => (
    <table className="positions-table">
      <thead>
        <tr>
          <th>Pos</th>
          <th className='division-title'>Equipo</th>
          <th>J</th>
          <th>V</th>
          <th>E</th>
          <th>D</th>
          <th>DG</th>
          <th>PTS</th>
        </tr>
      </thead>
      <tbody>
        {division.ranking.map((team) => {
          const hasValidUrl = team.url_wp && team.url_wp.trim().length > 0 && version != "us";
          return (
            <tr key={`${team.contestantShortName}-${team.contestantCode}`}>
              <td className="team-item">{team.rank}</td>
              <td className="team-item">
                <div
                  className='img-item'
                  {...(hasValidUrl && { onClick: () => openLinkParent(team.url_wp, false) })}
                >
                  <img
                    src={team.image}
                    alt={`Logo ${team.contestantShortName}`}
                    loading='lazy'
                    onError={(e) => e.target.src = 'assets/default.png'}
                  />
                  <span className={hasValidUrl ?'item-link' : ''}>{team.contestantShortName}</span>
                </div>
              </td>
              <td className="team-item">{team.matchesPlayed}</td>
              <td className="team-item">{team.matchesWon}</td>
              <td className="team-item">{team.matchesDrawn}</td>
              <td className="team-item">{team.matchesLost}</td>
              <td className="team-item">{team.goalDifference}</td>
              <td className="team-item">{team.points}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderTable = (tab) => {
    const divisions = tab.division.filter(div => div.ranking?.length > 0);
    
    if (divisions.length === 1) {
      return (
        <div className="table-content">
          {renderDivisionTable(divisions[0])}
        </div>
      );
    }

    // Múltiples divisiones - tabs para 2, accordion para más de 2
    const divisionStages = divisions.map((division, index) => ({
      id: division.name || division.id,
      name: division.name || `Grupo ${String.fromCharCode(65 + index)}`,
      division: [division]
    }));

    // Calcula el índice de la división activa basándose en las fechas
    const activeDivisionIndex = divisionStages.findIndex(div => 
      div.division[0].startDate && div.division[0].endDate && 
      isCurrentDateInRange(div.division[0].startDate, div.division[0].endDate)
    );
    const initialDivisionTab = activeDivisionIndex >= 0 ? activeDivisionIndex : 0;

    const useDivisionTabs = divisions.length === 2;

    return (
      <div className="table-content" style={{padding: '20px 10px' }}>
        {useDivisionTabs ? (
          <TabsTable
            key={tab.id || tab.name}
            stages={divisionStages}
            renderTable={(stage) => renderDivisionTable(stage.division[0])}
            variant={stages.length === 1 ? 'primary' : "nested"}
            initialActiveTab={initialDivisionTab}
          />
        ) : (
          <AccordionTable
            key={tab.id || tab.name}
            stages={divisionStages}
            renderTable={(stage) => renderDivisionTable(stage.division[0])}
            defaultOpen={0}
            variant="nested"
          />
        )}
      </div>
    );
  };

  if (stages.length === 0) {
    return <NoDataMessage title="Posiciones" />;
  }

  return (
    <>
      <div className="positions-table__header">
        <HeaderTable title="Posiciones" />
      </div>
      <div className="positions-table__sport">
        {useTabs ? (
          <TabsTable 
            stages={stages} 
            renderTable={renderTable} 
            variant="primary"
            initialActiveTab={initialActiveTab}
          />
        ) : (
          <AccordionTable stages={stages} renderTable={renderTable} variant="primary" />
        )}
      </div>
    </>
  );
}